import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { Button } from '@piplapps/pipl-ui';
import { connect } from 'react-redux';
import moment from 'moment';
import Popup from 'reactjs-popup';
import styles from './UsersSummary.module.less';
import { UsersSelectors } from '../../../Redux/modules/users/selectors';
import { UsersActions } from '../../../Redux/modules/users/actions';
import SendRemindersModal from './SendRemindersModal';
import UsersNotification from './UsersNotification';
import icon from '../../../../../static/img/white.svg';
import { writeToStorage } from '../../../Services/LocalStorageService';
import { localStorageKeys } from '../../../Constants/Common';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import UploadUsersListContainer from './UploadUsersListModals/UploadUsersListContainer';
import AddUserModal from './AddUserModal';

const MINUTES_TO_WAIT_UNTIL_SHOWING_NOTIFICATION = 60 * 24;

const UsersSummary = (props) => {
  const inviteNotificationShown = () => {
    const lastClosed = localStorage.getItem(localStorageKeys.INVITE_NOTIFICATION_CLOSED_AT);
    if (lastClosed === null) {
      return true;
    }
    return moment(new Date()).diff(moment(lastClosed), 'minutes') >= MINUTES_TO_WAIT_UNTIL_SHOWING_NOTIFICATION;
  };
  const [inviteNotificationShow, setInviteNotification] = useState(inviteNotificationShown());
  const modalStyle = {
    width: '500px',
    boxShadow: 'rgba(101, 101, 101, 0.28) 0px 4px 12px 0px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#ffffff',
  };

  return (
    <div>
      <div className={styles.textWrapper}>
        <div className={styles.text} id="users-growing-text">
          <div className={styles.buttonWrapperAddUser}>
            <AnalyticsHandler eventName={AnalyticsEventNames.CLICK_INVITE_USER_BUTTON}>
              <Button
                buttonClass="primary-blue"
                text={gettext('+ Invite user')}
                onClick={() => props.clickedAddUserButton()}
                id="add-user-button"
              />
            </AnalyticsHandler>
          </div>
          <Popup
            onClose={() => props.closeModal()}
            contentStyle={modalStyle}
            modal
            open={props.addUserModalOpen}
            closeOnDocumentClick={false}
          >
            {() => (
              <AddUserModal
                availableSubscriptions={props.availableSubscriptions}
                trialEndDate={props.trialEndDate}
                isTrial={props.isTrial}
              />
            )}
          </Popup>
        </div>

        <div className={styles.buttonsWrapper}>
          <div className={[styles.buttonWrapper, styles.uploadButton].join(' ')}>
            {!props.isUserListInProgress && (
              <AnalyticsHandler eventName={AnalyticsEventNames.CLICK_UPLOAD_USERS_LIST}>
                <Button
                  id="upload-users-button"
                  buttonClass="label-blue"
                  text={gettext('Upload users list')}
                  onClick={() => {
                    props.clickedUploadUsersListButton();
                  }}
                />
              </AnalyticsHandler>
            )}
          </div>
          <div className={styles.buttonWrapper}>
            <AnalyticsHandler eventName={AnalyticsEventNames.EXPORT_USERS_LIST}>
              <Button
                isLoading={props.exportIsLoading}
                loadingText={gettext('Generating...')}
                id="users-export-button"
                buttonClass="label-blue"
                text={gettext('Export users list')}
                onClick={props.exportUserList}
              />
            </AnalyticsHandler>
          </div>
        </div>
      </div>
      <div className={styles.summary}>
        {props.isLoading && (
          <div style={{ width: 350, lineHeight: 2 }}>
            <Skeleton height={15} count={3} />
          </div>
        )}
        {!props.isLoading && (
          <>
            {props.invitedUsersCount > 0 ? (
              <UsersNotification
                text={
                  props.invitedUsersCount === 1
                    ? gettext(`One user has not confirmed their invitation.`)
                    : gettext(`${props.invitedUsersCount} users have not confirmed their invitation.`)
                }
                icon={
                  <div className={styles.alert}>
                    <img alt="" src={icon} />
                  </div>
                }
                action={
                  <AnalyticsHandler eventName={AnalyticsEventNames.SENT_REMINDERS}>
                    <Button
                      buttonClass="label-blue"
                      text={gettext('Send a reminder')}
                      onClick={() => {
                        props.clickSendReminders();
                      }}
                      id="send-reminder-button"
                    />
                  </AnalyticsHandler>
                }
                notificationShown={inviteNotificationShow}
                removeNotification={() => {
                  writeToStorage(localStorageKeys.INVITE_NOTIFICATION_IS_SHOWN, false);
                  localStorage.setItem(localStorageKeys.INVITE_NOTIFICATION_CLOSED_AT, new Date());
                  setInviteNotification(false);
                }}
              />
            ) : null}
            {props.isUserListPendingShowing ? (
              <UsersNotification
                text={gettext('We are processing the new user list. We will notify you when it’s done. ')}
                icon={
                  <div className={[styles.alert, styles.blue].join(' ')}>
                    <img alt="" src={icon} />
                  </div>
                }
                action={
                  <AnalyticsHandler eventName={AnalyticsEventNames.OPEN_USER_UPLOAD_PROGRESS}>
                    <Button
                      buttonClass="label-blue"
                      text={gettext('Progress')}
                      onClick={() => {
                        props.clickedUploadUsersListButton();
                      }}
                      id="open-progress-button"
                    />
                  </AnalyticsHandler>
                }
                notificationShown={props.isUserListPendingShowing}
                removeNotification={() => {
                  writeToStorage(localStorageKeys.USER_LIST_NOTIFICATION_IS_SHOWN, false);
                  props.closeUserListPendingNotice();
                }}
              />
            ) : null}
          </>
        )}
        <UploadUsersListContainer
          onClose={props.closeModal}
          open={props.multiAssignModelOpen}
          availableSubscriptions={props.availableSubscriptions}
        />
        <SendRemindersModal
          onClose={props.closeModal}
          open={props.sendRemindersModalOpen}
          users={props.users}
          sendReminders={props.sendReminders}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    exportIsLoading: UsersSelectors.exportIsLoadingSelector(state),
    multiAssignModelOpen: UsersSelectors.multiAssignModelSelector(state),
    sendRemindersModalOpen: UsersSelectors.sendRemindersModalSelector(state),
    isUserListPendingShowing: UsersSelectors.isUserListNoticeShowSelector(state),
    isUserListInProgress: UsersSelectors.isUploadUsersListInProgressSelector(state),
    addUserModalOpen: UsersSelectors.addUserModalSelector(state),
  };
};

const mapDispatchToProps = {
  exportUserList: UsersActions.exportUserList,
  sendReminders: UsersActions.sendReminders,
  clickedUploadUsersListButton: UsersActions.clickedUploadUsersListButton,
  closeModal: UsersActions.clickedCloseModalButton,
  closeUserListPendingNotice: UsersActions.closeUserListPendingNotice,
  clickSendReminders: UsersActions.clickedSendReminders,
  clickedAddUserButton: UsersActions.clickedAddUserButton,
};

UsersSummary.propTypes = {
  isLoading: PropTypes.bool,
  exportIsLoading: PropTypes.bool,
  exportUserList: PropTypes.func,
  clickedUploadUsersListButton: PropTypes.func,
  clickedAddUserButton: PropTypes.func,
  invitedUsersCount: PropTypes.number,
  sendReminders: PropTypes.func,
  sendRemindersModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  multiAssignModelOpen: PropTypes.bool,
  availableSubscriptions: PropTypes.number,
  isUserListPendingShowing: PropTypes.bool,
  closeUserListPendingNotice: PropTypes.func,
  clickSendReminders: PropTypes.func,
  users: PropTypes.array,
  isUserListInProgress: PropTypes.bool,
  isTrial: PropTypes.bool,
  addUserModalOpen: PropTypes.bool,
  trialEndDate: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersSummary);
